import type { StepperLimits } from '../types';

// ============================================================================
// VALIDACIONES Y CÁLCULOS
// ============================================================================

/**
 * Valida si un valor está dentro de los límites permitidos
 *
 * @param value - Valor a validar
 * @param min - Valor mínimo permitido
 * @param max - Valor máximo permitido
 * @returns true si el valor está dentro de los límites
 *
 * @example
 * isValueInRange(5, 0, 10) // true
 * isValueInRange(-1, 0, 10) // false
 */
export function isValueInRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

export function clampValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Calcula el siguiente valor válido al incrementar
 *
 * @param currentValue - Valor actual
 * @param step - Paso de incremento
 * @param max - Valor máximo permitido
 * @returns Nuevo valor después del incremento
 *
 * @example
 * calculateIncrementValue(5, 1, 10) // 6
 * calculateIncrementValue(9, 1, 10) // 10
 * calculateIncrementValue(10, 1, 10) // 10 (no cambia)
 */
export function calculateIncrementValue(currentValue: number, step: number, max: number): number {
  const newValue = currentValue + step;
  return Math.min(newValue, max);
}

/**
 * Calcula el siguiente valor válido al decrementar
 *
 * @param currentValue - Valor actual
 * @param step - Paso de decremento
 * @param min - Valor mínimo permitido
 * @returns Nuevo valor después del decremento
 *
 * @example
 * calculateDecrementValue(5, 1, 0) // 4
 * calculateDecrementValue(1, 1, 0) // 0
 * calculateDecrementValue(0, 1, 0) // 0 (no cambia)
 */
export function calculateDecrementValue(currentValue: number, step: number, min: number): number {
  const newValue = currentValue - step;
  return Math.max(newValue, min);
}

/**
 * Verifica si se puede incrementar el valor
 *
 * @param currentValue - Valor actual
 * @param max - Valor máximo permitido
 * @returns true si se puede incrementar
 */
export function canIncrement(currentValue: number, max: number): boolean {
  return currentValue < max;
}

/**
 * Verifica si se puede decrementar el valor
 *
 * @param currentValue - Valor actual
 * @param min - Valor mínimo permitido
 * @returns true si se puede decrementar
 */
export function canDecrement(currentValue: number, min: number): boolean {
  return currentValue > min;
}

/**
 * Normaliza los límites del stepper con valores por defecto
 *
 * @param limits - Límites parciales del stepper
 * @returns Límites completos con valores por defecto
 */
export function normalizeLimits(limits: Partial<StepperLimits>): StepperLimits {
  return {
    min: limits.min ?? Number.NEGATIVE_INFINITY,
    max: limits.max ?? Infinity,
    step: limits.step ?? 1,
  };
}

/**
 * Parsea un valor de input a número válido
 *
 * @param inputValue - Valor del input como string
 * @param fallback - Valor de respaldo si el parsing falla
 * @returns Número parseado o valor de respaldo
 */
export function parseInputValue(inputValue: string, fallback: number): number {
  const trimmed = inputValue.trim();

  // Si el usuario borra todo, considerar el valor como 0 para permitir resetear fácilmente
  if (trimmed === '') {
    return 0;
  }

  // Permitir números negativos y decimales
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? fallback : parsed;
}
